/**
 * 力学计算模型：滑动摩擦力 f = μN。
 *
 * 接触面积/朝向（平放/竖放）不进公式——这不是简化，是物理事实（滑动摩擦力与接触面积无关）。
 * "实验4·接触面积"的"基本不变"结论因此是模型的自然结果，不是写死的剧本（见 tech/108 §4.3）。
 */

export const SURFACE_MU: Record<string, number> = {
  wood: 0.35,
  cloth: 0.55,
  towel: 0.7,
  glass: 0.2,
};

/** 木块自重（未加砝码时的压力，单位 N）。 */
export const BLOCK_WEIGHT_N = 10;
/** 每个砝码的重量（单位 N）。 */
export const WEIGHT_UNIT_N = 5;

export function computeNormalForce(weightCount: number): number {
  return BLOCK_WEIGHT_N + weightCount * WEIGHT_UNIT_N;
}

export function computeFriction(normalForceN: number, surfaceId: string): number {
  const mu = SURFACE_MU[surfaceId] ?? SURFACE_MU.wood;
  return Math.round(normalForceN * mu * 100) / 100;
}

/**
 * 最大静摩擦系数：略高于滑动摩擦系数（物理事实：起步比匀速滑动更"粘"）。
 * 供渲染层判断"拖到多大的力才能真正拉动木块"，不影响 `computeFriction` 的既有语义
 * ——记录的滑动摩擦力读数仍然只由 `computeFriction` 给出。
 */
export const SURFACE_MU_STATIC: Record<string, number> = {
  wood: 0.42,
  cloth: 0.62,
  towel: 0.78,
  glass: 0.26,
};

export function computeMaxStaticFriction(normalForceN: number, surfaceId: string): number {
  const muS = SURFACE_MU_STATIC[surfaceId] ?? SURFACE_MU_STATIC.wood;
  return Math.round(normalForceN * muS * 100) / 100;
}

/**
 * 匀速判定容差（单位 N）：滑动过程中，拉力与滑动摩擦力的差落在这个范围内，即认为木块在
 * 做「匀速直线运动」（合力 ≈ 0）。这是 P0-2 的物理判定——不是「拉过最大静摩擦就算数」，
 * 而是必须把拉力稳定到滑动摩擦力附近（起步后回落到匀速区间）才能读出准确示数。
 */
export const UNIFORM_TOLERANCE_N = 0.5;

export type PullPhase =
  | "idle"
  | "static"
  | "breaking"
  | "starting"
  | "uniform"
  | "accelerating"
  | "decelerating";

export interface PullState {
  phase: PullPhase;
  /** 木块是否已经开始滑动（本次拖动过程中拉力曾超过最大静摩擦）。 */
  moving: boolean;
  /** 是否处于「匀速」区间，可作为有效读数提交。 */
  uniform: boolean;
  kineticFriction: number;
  maxStatic: number;
}

/**
 * 拉力状态分类：木块起步前处于静摩擦阶段，起步后进入滑动摩擦阶段。
 * - `hasStarted=false`：拉力 < 最大静摩擦 → 未动（static/breaking），≥ → 刚起步（starting）。
 * - `hasStarted=true`：拉力 ≈ 滑动摩擦 → 匀速（uniform）；> → 加速（accelerating）；< → 减速（decelerating）。
 */
export function classifyPull(
  appliedForce: number,
  normalForce: number,
  surfaceId: string,
  hasStarted: boolean,
): PullState {
  const maxStatic = computeMaxStaticFriction(normalForce, surfaceId);
  const kineticFriction = computeFriction(normalForce, surfaceId);

  let phase: PullPhase;
  if (appliedForce <= 0) {
    phase = "idle";
  } else if (!hasStarted) {
    if (appliedForce >= maxStatic) phase = "starting";
    else if (appliedForce >= maxStatic * 0.7) phase = "breaking";
    else phase = "static";
  } else if (Math.abs(appliedForce - kineticFriction) <= UNIFORM_TOLERANCE_N) {
    phase = "uniform";
  } else if (appliedForce > kineticFriction) {
    phase = "accelerating";
  } else {
    phase = "decelerating";
  }

  return {
    phase,
    moving: hasStarted || phase === "starting",
    uniform: phase === "uniform",
    kineticFriction,
    maxStatic,
  };
}
